import React, { useRef, useState, useEffect } from "react";
import { Chess } from "chess.js";
import { Chessboard } from "react-chessboard";

const GAME_TIME = 10 * 60;
const BOT_AVATAR = "https://img.icons8.com/color/96/000000/bot.png";
const USER_AVATAR = "https://img.icons8.com/ios-filled/96/user-male-circle.png";

// --- NEW: Play sounds ---
function playSound(type = "move") {
  const sounds = {
    move: "/sounds/xo-click.mp3",
    capture: "/sounds/bot-play.mp3",
    check: "/sounds/lose.mp3",
    checkmate: "/sounds/lose.mp3",
  };
  const audio = new window.Audio(sounds[type] || sounds.move);
  audio.volume = 0.5;
  audio.play();
}

// --- Use public path for Stockfish if needed ---
function createStockfish() {
  return new window.Worker("/stockfish.js");
}

function getResultMsg(result) {
  if (result === "win")
    return <span className="text-lime-400">🎉 You Win!</span>;
  if (result === "lose")
    return <span className="text-pink-400">😭 You Lose!</span>;
  if (result === "draw")
    return <span className="text-yellow-300">🤝 Draw!</span>;
  if (result === "timeout")
    return <span className="text-yellow-300">⏰ Time Out! Draw!</span>;
  return null;
}

// --- NEW: Helper to find checked king's square (algebraic) ---
function findKingSquare(game, color) {
  const board = game.board();
  for (let r = 0; r < 8; r++) {
    for (let c = 0; c < 8; c++) {
      const piece = board[r][c];
      if (piece && piece.type === "k" && piece.color === color) {
        return "abcdefgh"[c] + (8 - r);
      }
    }
  }
  return null;
}

export default function ChessGameVsComputer({ user, onBack }) {
  const [side, setSide] = useState("w");
  const [game, setGame] = useState(() => new Chess());
  const [fen, setFen] = useState("start");
  const [turn, setTurn] = useState("w");
  const [result, setResult] = useState(null);
  const [userTimer, setUserTimer] = useState(GAME_TIME);
  const [botTimer, setBotTimer] = useState(GAME_TIME);
  const [isGameOver, setIsGameOver] = useState(false);
  const [showMoves, setShowMoves] = useState(false);
  const [moveSquares, setMoveSquares] = useState({});
  const [selected, setSelected] = useState(null);

  // --- NEW: Track check state ---
  const [inCheck, setInCheck] = useState(false);
  const [checkedColor, setCheckedColor] = useState(null);

  const userTimerRef = useRef();
  const botTimerRef = useRef();
  const stockfishRef = useRef();

  // -------- Timer Logic --------
  useEffect(() => {
    if (isGameOver) {
      clearInterval(userTimerRef.current);
      clearInterval(botTimerRef.current);
      return;
    }
    if (turn === side && !isGameOver) {
      userTimerRef.current = setInterval(() => {
        setUserTimer((t) => {
          if (t <= 1) {
            setResult("timeout");
            setIsGameOver(true);
            playSound("checkmate");
            return 0;
          }
          return t - 1;
        });
      }, 1000);
      return () => clearInterval(userTimerRef.current);
    } else if (turn !== side && !isGameOver) {
      botTimerRef.current = setInterval(() => {
        setBotTimer((t) => {
          if (t <= 1) {
            setResult("win"); // Bot timeout: user wins (should never happen)
            setIsGameOver(true);
            playSound("checkmate");
            return 0;
          }
          return t - 1;
        });
      }, 1000);
      return () => clearInterval(botTimerRef.current);
    }
    // Cleanup
    return () => {
      clearInterval(userTimerRef.current);
      clearInterval(botTimerRef.current);
    };
  }, [turn, side, isGameOver]);

  // -------- Stockfish/Bot Engine --------
  useEffect(() => {
    stockfishRef.current = createStockfish();
    return () => stockfishRef.current && stockfishRef.current.terminate();
  }, []);

  // On bot turn, request Stockfish move
  useEffect(() => {
    if (isGameOver) return;
    if (turn !== side) {
      setTimeout(() => {
        getBotMove();
      }, 700);
    }
    // eslint-disable-next-line
  }, [turn, fen, isGameOver, side]);

  function getBotMove() {
    const engine = stockfishRef.current;
    engine.postMessage("ucinewgame");
    engine.postMessage(`position fen ${game.fen()}`);
    engine.postMessage("go movetime 1200");
    engine.onmessage = function (event) {
      const line = event.data || event;
      if (typeof line !== "string") return;
      if (line.startsWith("bestmove")) {
        const parts = line.split(" ");
        const move = parts[1];
        if (move === "(none)") {
          finishGame();
          return;
        }
        const newGame = new Chess(game.fen());
        const moveObj = newGame.move({
          from: move.substring(0, 2),
          to: move.substring(2, 4),
          promotion: move.length > 4 ? move[4] : "q",
        });
        playSound(moveObj && moveObj.flags.includes("c") ? "capture" : "move");
        setGame(newGame);
        setFen(newGame.fen());
        setTurn(side);

        // --- Update check state ---
        if (newGame.isCheckmate()) {
          setResult("lose");
          setIsGameOver(true);
          playSound("checkmate");
          setInCheck(false);
        } else if (newGame.inCheck()) {
          setInCheck(true);
          setCheckedColor(side); // user's king is checked
          playSound("check");
        } else {
          setInCheck(false);
          setCheckedColor(null);
        }
      }
    };
  }

  // --- Track check status for UI ---
  useEffect(() => {
    // Only update on new FEN or turn
    if (game.isCheckmate()) {
      setInCheck(false);
      setCheckedColor(null);
      return;
    }
    if (game.inCheck()) {
      setInCheck(true);
      setCheckedColor(game.turn());
      playSound("check");
    } else {
      setInCheck(false);
      setCheckedColor(null);
    }
    // eslint-disable-next-line
  }, [fen, turn]);

  // --- Show moves for clicked piece ---
  function handleSquareClick(square) {
    if (isGameOver || turn !== side) return;
    const moves = game.moves({ square, verbose: true });
    if (selected && moveSquares[selected]?.includes(square)) {
      handleUserMove(selected, square);
      setMoveSquares({});
      setSelected(null);
    } else if (moves.length > 0) {
      setSelected(square);
      setMoveSquares({
        [square]: moves.map((m) => m.to),
      });
    } else {
      setSelected(null);
      setMoveSquares({});
    }
  }

  function handleUserMove(from, to) {
    if (isGameOver || turn !== side) return false;
    const newGame = new Chess(game.fen());
    const move = newGame.move({ from, to, promotion: "q" });
    if (move == null) return false;
    playSound(move.flags.includes("c") ? "capture" : "move");
    setGame(newGame);
    setFen(newGame.fen());
    setTurn(side === "w" ? "b" : "w");
    if (newGame.isCheckmate()) {
      setResult("win");
      setIsGameOver(true);
      playSound("checkmate");
      setInCheck(false);
      setCheckedColor(null);
    } else if (newGame.inCheck()) {
      setInCheck(true);
      setCheckedColor(side === "w" ? "b" : "w");
      playSound("check");
    } else {
      setInCheck(false);
      setCheckedColor(null);
    }
    return true;
  }

  function onDrop(src, dest) {
    if (isGameOver || turn !== side || showMoves) return false;
    return handleUserMove(src, dest);
  }

  function finishGame() {
    if (game.isCheckmate()) {
      setResult(turn === side ? "win" : "lose");
      setIsGameOver(true);
      playSound("checkmate");
      setInCheck(false);
      setCheckedColor(null);
    } else {
      setResult("draw");
      setIsGameOver(true);
      playSound("check");
      setInCheck(false);
      setCheckedColor(null);
    }
  }

  function handlePlayAgain() {
    setGame(new Chess());
    setFen("start");
    setTurn(side === "w" ? "w" : "b");
    setResult(null);
    setIsGameOver(false);
    setUserTimer(GAME_TIME);
    setBotTimer(GAME_TIME);
    setMoveSquares({});
    setSelected(null);
    setInCheck(false);
    setCheckedColor(null);
  }

  function handleSwitchSides() {
    setSide(side === "w" ? "b" : "w");
    handlePlayAgain();
  }

  function formatTimer(sec) {
    sec = Math.max(sec, 0);
    const m = String(Math.floor(sec / 60)).padStart(2, "0");
    const s = String(sec % 60).padStart(2, "0");
    return `${m}:${s}`;
  }

  // --- Improved move/capture highlight ---
  function getCustomSquareStyles() {
    const styles = {};
    // Show moves highlight
    if (showMoves && selected) {
      styles[selected] = {
        boxShadow: "0 0 0 4px #a3e635cc, 0 2px 12px #bbf7d0",
        zIndex: 2,
      };
      const moves = game.moves({ square: selected, verbose: true });
      moves.forEach((m) => {
        styles[m.to] = {
          position: "relative",
          zIndex: 3,
          background: m.flags.includes("c")
            ? "radial-gradient(circle at 50% 50%, #f87171bb 40%, transparent 42%)"
            : "radial-gradient(circle at 50% 50%, #a3e635bb 30%, transparent 32%)",
          border: m.flags.includes("c")
            ? "2px solid #f87171"
            : "2px solid #a3e63544",
        };
      });
    }
    // --- NEW: Highlight king if in check ---
    if (inCheck && checkedColor) {
      const kingSq = findKingSquare(game, checkedColor);
      if (kingSq) {
        styles[kingSq] = {
          ...styles[kingSq],
          boxShadow: "0 0 0 6px #ef4444dd, 0 2px 16px #f87171cc",
          zIndex: 10,
          background:
            "radial-gradient(circle at 50% 50%, #fecaca99 80%, transparent 90%)",
        };
      }
    }
    return styles;
  }

  // --- UI ---
  return (
    <div className="flex flex-col items-center justify-center min-h-screen py-10 px-2 bg-gradient-to-br from-slate-950 to-green-950">
      <div className="bg-slate-900 border-2 border-lime-400 rounded-2xl shadow-2xl max-w-lg w-full flex flex-col items-center p-6">
        <div className="text-lime-400 font-extrabold text-3xl mb-1 text-center">
          ♟️ Chess vs Computer
        </div>
        <div className="flex gap-3 mb-2 items-center justify-center">
          <span
            className={
              "px-3 py-1 rounded-xl text-xs font-bold " +
              (turn === side
                ? "bg-lime-400 text-slate-900"
                : "bg-slate-700 text-lime-300")
            }
          >
            {turn === side ? "Your Turn" : "Computer's Turn"}
          </span>
          <span className="text-sm">
            <span className="text-lime-400">Time:</span>{" "}
            <span
              className={
                turn === side
                  ? "text-lime-400 font-bold"
                  : "text-slate-300 font-bold"
              }
            >
              {formatTimer(userTimer)}
            </span>
            <span className="mx-1 text-slate-500">/</span>
            <span
              className={
                turn !== side
                  ? "text-pink-400 font-bold"
                  : "text-slate-300 font-bold"
              }
            >
              {formatTimer(botTimer)}
            </span>
          </span>
        </div>
        {/* --- CHECK/CHECKMATE Message --- */}
        {inCheck && !isGameOver && (
          <div className="mt-1 mb-2 text-2xl font-extrabold text-red-500 animate-pulse drop-shadow-lg">
            ⚠️ Check!
          </div>
        )}
        {result === "lose" && (
          <div className="mt-1 mb-2 text-2xl font-extrabold text-red-500 animate-pulse drop-shadow-lg">
            ♛ Checkmate! You Lose!
          </div>
        )}
        {result === "win" && (
          <div className="mt-1 mb-2 text-2xl font-extrabold text-lime-400 animate-pulse drop-shadow-lg">
            🎉 Checkmate! You Win!
          </div>
        )}
        <div className="flex justify-center items-center gap-8 mb-2">
          <div className="flex flex-col items-center">
            <img
              src={side === "w" ? user?.photoURL || USER_AVATAR : BOT_AVATAR}
              alt="you"
              className="w-12 h-12 rounded-full border-2 border-lime-300"
            />
            <span className="mt-1 font-semibold text-lime-300 text-sm">
              {side === "w" ? user?.displayName || "You" : "Computer"}
            </span>
            <span className="text-xs text-slate-400">
              {side === "w" ? "White" : "Black"}
            </span>
          </div>
          <div className="text-2xl font-bold text-lime-400">vs</div>
          <div className="flex flex-col items-center">
            <img
              src={side !== "w" ? user?.photoURL || USER_AVATAR : BOT_AVATAR}
              alt="bot"
              className="w-12 h-12 rounded-full border-2 border-slate-400"
            />
            <span className="mt-1 font-semibold text-slate-300 text-sm">
              {side !== "w" ? user?.displayName || "You" : "Computer"}
            </span>
            <span className="text-xs text-slate-400">
              {side !== "w" ? "White" : "Black"}
            </span>
          </div>
        </div>
        <div className="my-2">
          <Chessboard
            position={fen}
            onPieceDrop={onDrop}
            boardWidth={360}
            boardOrientation={side === "w" ? "white" : "black"}
            arePiecesDraggable={!isGameOver && turn === side && !showMoves}
            onSquareClick={showMoves ? handleSquareClick : undefined}
            customSquareStyles={getCustomSquareStyles()}
          />
        </div>
        <div className="flex flex-row gap-2 mb-2">
          <button
            className={
              "px-4 py-2 rounded-lg font-bold transition " +
              (showMoves
                ? "bg-lime-400 text-slate-900"
                : "bg-slate-700 text-lime-300 hover:bg-slate-600")
            }
            onClick={() => {
              setShowMoves((v) => !v);
              setSelected(null);
              setMoveSquares({});
            }}
          >
            {showMoves ? "Disable Show Moves" : "Show Moves"}
          </button>
          <button
            className="px-4 py-2 rounded-lg font-bold bg-slate-700 text-lime-300 hover:bg-slate-600 disabled:opacity-40 disabled:cursor-not-allowed"
            onClick={handleSwitchSides}
            disabled={game.history().length > 0 || isGameOver}
            title={
              game.history().length > 0
                ? "Can't switch after game started"
                : "Switch sides"
            }
          >
            Switch Sides
          </button>
        </div>
        {result && (
          <div
            className={
              "mt-6 text-2xl font-bold mb-1 text-center " +
              (result === "lose" || result === "timeout"
                ? "text-pink-400 animate-pulse"
                : result === "win"
                ? "text-lime-400"
                : "text-yellow-300")
            }
          >
            {getResultMsg(result)}
          </div>
        )}
        <div className="flex flex-col gap-2 mt-6 w-full max-w-xs">
          {isGameOver && (
            <button
              className="px-8 py-3 rounded-xl bg-lime-400 text-slate-900 font-extrabold shadow hover:bg-lime-300 transition"
              onClick={handlePlayAgain}
            >
              Play Again
            </button>
          )}
          <button
            className="px-8 py-2 rounded-xl bg-slate-700 text-lime-300 font-bold hover:bg-slate-600 transition"
            onClick={onBack}
          >
            ← Back to Lobby
          </button>
        </div>
      </div>
    </div>
  );
}
